import { useCallback } from "react";
import { useGetContext } from "@onefront/forrestjs-react-root";
import { buildUrl } from "../utils/build-url";
import { useAuth } from "../../service-auth/state/use-auth";

/**
 * Get the functions to convert URN into proxy call to redirect users.
 * `TargetUrl` should be used when only the redirection is needed.
 * `JumpUrl` should be used when the user should go throught the login app to obtain a new session
 *
 * @returns {{createTargetUrl: targetUrl, createJumpUrl: jumpUrl}} Object contains two synchronous function to convert
 *  the URN into a proxy url to redirect the user.
 */
export const useProxy = () => {
  const proxyBaseUrl = useGetContext("one.axios.proxy.baseUrl");
  const { sessionData } = useAuth();

  /**
   * @param {string} target - URN (or URL as well) to be converted into a proxy url to redirect the user
   * @param {Array} params - Array of string to added as additional query parameters into the request
   *
   * @returns {string} The proxy url to be called
   */
  const targetUrl = useCallback(
    (target, params) => {
      const url = addParamsToTarget(target, params);

      return createProxyUrl(url, proxyBaseUrl);
    },
    [proxyBaseUrl]
  );

  /**
   * @param {string} target - URN (or URL as well) to be converted into a proxy url which
   *   first go throught loginApi to create a new session for the target app
   *
   * @returns {string} The proxy url to be called
   */
  const jumpUrl = useCallback(
    (target) => {
      const url = buildUrnToLoginApi(target, sessionData);

      return createProxyUrl(url, proxyBaseUrl);
    },
    [sessionData, proxyBaseUrl]
  );

  return {
    createTargetUrl: targetUrl,
    createJumpUrl: jumpUrl
  };
};

function createProxyUrl(target, proxyBaseUrl) {
  const redirectUrl = `redirect?to=${encodeURIComponent(target)}`;

  return buildUrl({ target, proxyBaseUrl, stringToJoin: redirectUrl });
}

const buildUrnToLoginApi = (target, sessionData) => {
  const [appId, deepLink] = target.split("://");
  const data = sessionData ? { state: btoa(JSON.stringify(sessionData)) } : {};
  const urlParams = new URLSearchParams({
    ...{
      serviceId: appId
    },
    ...data
  });

  if (deepLink) urlParams.append("deepLink", `/${deepLink}`);

  const url = `loginApi://jump?${urlParams.toString()}`;
  return url;
};

function addParamsToTarget(target, params) {
  if (target.indexOf("://") >= 0) {
    return params ? `${target}?${params?.join("&")}` : target;
  }

  return params ? `${target}://?${params?.join("&")}` : target;
}
